import React, { useState, useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, LabelList, ComposedChart, Area, Line, ScatterChart, Scatter, ZAxis
} from 'recharts';
import { Badge, Table, Button, Form } from 'react-bootstrap';
import {
  FaCalendarAlt, FaExclamationTriangle, FaFilter, FaSlidersH, FaDollarSign, FaRedo
} from 'react-icons/fa';

const COLORS = [
  '#b54d7ac9', '#d1a054c9', '#409da0cb', '#a81d32cb', '#63a346c3', '#2a7296c6',
  '#d1a054c9', '#7b5ba1c9', '#46a38bc3', '#d16a54cb', '#54a0d1cb', '#8da346c3',
  '#a34692c3', '#46a35bc3', '#a37e46c3', '#4648a3c3', '#a34646c3', '#41b3a3c9',
  '#e27d60c9', '#8e8d8ac9', '#c2b9b0c9', '#7395aec9'
];

// --- MAIN COMPONENT ---
const AdminCheckReportz = ({ profiles = [], levels = [] }) => {
  // TOGGLE FILTER STATE (ONLY DATE FILTER REMAINS)
  const [selectedExpWindow, setSelectedExpWindow] = useState('all'); // 'all', '30days', '60days', 'thisMonth'
  const [retentionSimulation, setRetentionSimulation] = useState(0);

  const today = useMemo(() => new Date(), []);

  const levelLookup = useMemo(() => {
    return levels.reduce((acc, level) => {
      acc[level.id.toLowerCase().trim()] = {
        cost: Number(level.cost) || 0,
        lateFee: Number(level.lateFee) || 0,
        isFlagged: level.account_flag === true
      };
      return acc;
    }, {});
  }, [levels]);

  const baseProfiles = useMemo(() => {
    return profiles.filter(p => Boolean(p.member) || Boolean(p.isNew));
  }, [profiles]);

  // TOGGLE-BASED FILTERING LOGIC
  const filteredProfiles = useMemo(() => {
    return baseProfiles.filter(m => {
      // Expiration check calculation
      let isExpired = false;
      let daysToExp = null;
      let isThisMonth = false;

      if (m.expirationDate) {
        const expDate = m.expirationDate.toDate ? m.expirationDate.toDate() : new Date(m.expirationDate);
        if (expDate < today) isExpired = true;

        daysToExp = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        isThisMonth = expDate.getMonth() === today.getMonth() && expDate.getFullYear() === today.getFullYear();
      }

      // Expiration Window Toggle Filter
      if (selectedExpWindow === '30days' && (daysToExp === null || daysToExp < 0 || daysToExp > 30)) return false;
      if (selectedExpWindow === '60days' && (daysToExp === null || daysToExp < 0 || daysToExp > 60)) return false;
      if (selectedExpWindow === 'thisMonth' && !isThisMonth) return false;

      return true;
    });
  }, [baseProfiles, selectedExpWindow, today]);

  const reportData = useMemo(() => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const statusCats = {
      flagged: { name: 'Flagged', list: [] },
      incidents: { name: 'Incidents', list: [] },
      pending: { name: 'Pending', list: [] },
      expired: { name: 'Expired', list: [] },
      awaitingPayment: { name: 'Awaiting Payment', list: [] },
      active: { name: 'Active', list: [] },
    };

    const roleCats = {
      admins: { name: 'Admins', list: [] },
      board: { name: 'Board', list: [] },
      employees: { name: 'Employees', list: [] },
    };

    const financialBreakdown = {};
    const lostRevenueBreakdown = {};
    const expiringSoonList = [];

    const membershipTypes = [...new Set(filteredProfiles
      .filter(p => !levelLookup[(p.type || "").toLowerCase().trim()]?.isFlagged)
      .map(p => p.type || "Unassigned")
    )];

    const monthlyData = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const item = {
        month: monthNames[d.getMonth()],
        year: d.getFullYear().toString().slice(-2),
        fullLabel: `${monthNames[d.getMonth()]} '${d.getFullYear().toString().slice(-2)}`,
        totalRevenue: 0,
        memberCount: 0
      };
      membershipTypes.forEach(t => { item[t] = 0; });
      return item;
    });

    filteredProfiles.forEach(user => {
      const type = user.type || "Unassigned";
      const typeLower = type.toLowerCase().trim();
      const userLevel = levelLookup[typeLower] || { cost: 0, lateFee: 0, isFlagged: false };
      const fullName = user.name || user.displayName || "Unknown User";
      const userRef = { ...user, fullName };

      if (user.isAdmin) roleCats.admins.list.push(userRef);
      if (user.board) roleCats.board.list.push(userRef);
      if (typeLower === 'employee') roleCats.employees.list.push(userRef);

      let isExpired = false;
      if (user.expirationDate) {
        const expDate = user.expirationDate.toDate ? user.expirationDate.toDate() : new Date(user.expirationDate);
        if (expDate < today) isExpired = true;

        const daysToExp = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        if (daysToExp >= 0 && daysToExp <= 60 && !isExpired) {
          expiringSoonList.push({ ...userRef, daysToExp });
        }

        if (!userLevel.isFlagged && !user.hasIncident) {
          const monthDiff = (expDate.getFullYear() - today.getFullYear()) * 12 + (expDate.getMonth() - today.getMonth());
          if (monthDiff >= 0 && monthDiff < 12) {
            const amount = (userLevel.cost + userLevel.lateFee);
            monthlyData[monthDiff][type] += amount;
            monthlyData[monthDiff].totalRevenue += amount;
            monthlyData[monthDiff].memberCount += 1;
          }
        }
      }

      if (user.hasIncident) {
        statusCats.incidents.list.push(userRef);
      } else if (userLevel.isFlagged) {
        statusCats.flagged.list.push(userRef);
      } else if (user.member === false && user.membershipStatus === "active") {
        statusCats.pending.list.push(userRef);
      } else if (user.member === false && user.membershipStatus !== "active") {
        statusCats.awaitingPayment.list.push(userRef);
      } else {
        if (isExpired) {
          statusCats.expired.list.push(userRef);
        } else {
          statusCats.active.list.push(userRef);
        }
      }

      if (!userLevel.isFlagged && !user.hasIncident) {
        if (!financialBreakdown[type]) financialBreakdown[type] = { count: 0, revenue: 0, levelCost: userLevel.cost };
        financialBreakdown[type].count += 1;

        const isReturningMemberExpired = isExpired && user.member === true;
        const userValue = isReturningMemberExpired ? (userLevel.cost + userLevel.lateFee) : userLevel.cost;

        financialBreakdown[type].revenue += userValue;

        const isNewAwaitingPayment = user.member === false && user.membershipStatus !== "active";
        if (isExpired || isNewAwaitingPayment) {
          if (!lostRevenueBreakdown[type]) lostRevenueBreakdown[type] = { count: 0, loss: 0, isNew: false };

          lostRevenueBreakdown[type].count += 1;
          lostRevenueBreakdown[type].loss += userValue;
          if (isNewAwaitingPayment) lostRevenueBreakdown[type].isNew = true;
        }
      }
    });

    const totalRevenue = Object.values(financialBreakdown).reduce((sum, item) => sum + item.revenue, 0);
    const totalLoss = Object.values(lostRevenueBreakdown).reduce((sum, item) => sum + item.loss, 0);
    const netPotential = totalRevenue - totalLoss;
    const collectionRate = totalRevenue > 0 ? Math.round((netPotential / totalRevenue) * 100) : 0;
    const leadershipCount = new Set([...roleCats.admins.list.map(u => u.fullName), ...roleCats.board.list.map(u => u.fullName)]).size;

    const activeCount = statusCats.active.list.length;
    const arpu = activeCount > 0 ? (totalRevenue / activeCount).toFixed(2) : 0;
    const churnRate = filteredProfiles.length > 0 ? ((statusCats.expired.list.length / filteredProfiles.length) * 100).toFixed(1) : 0;

    const simulatedRevenue = Math.round(netPotential * (1 + retentionSimulation / 100));

    const statusData = Object.values(statusCats).map(c => ({ name: c.name, value: c.list.length })).filter(i => i.value > 0);
    const roleData = Object.values(roleCats).map(c => ({ name: c.name, value: c.list.length })).filter(i => i.value > 0);

    const bubbleData = Object.entries(financialBreakdown).map(([name, data], index) => {
      const tierWeight = totalRevenue > 0 ? ((data.revenue / totalRevenue) * 100).toFixed(1) : 0;
      return {
        name,
        revenue: data.revenue,
        members: data.count,
        avg: data.count > 0 ? (data.revenue / data.count).toFixed(2) : 0,
        weight: tierWeight,
        cost: data.levelCost,
        z: data.count * 15,
        fill: COLORS[index % COLORS.length]
      };
    });

    return {
      statusCats,
      roleCats,
      financialBreakdown,
      lostRevenueBreakdown,
      expiringSoonList,
      monthlyData,
      membershipTypes,
      totalRevenue,
      totalLoss,
      netPotential,
      simulatedRevenue,
      collectionRate,
      leadershipCount,
      activeCount,
      arpu,
      churnRate,
      statusData,
      roleData,
      bubbleData
    };
  }, [filteredProfiles, levelLookup, today, retentionSimulation]);

  const resetFilters = () => {
    setSelectedExpWindow('all');
  };

  const renderCustomLegend = (payload, justify = "justify-content-start", ps = "ps-4") => (
    <div className={`d-flex flex-wrap ${justify} ${ps} gap-2 mb-3`} style={{ fontSize: '10px', color: '#888' }}>
      {payload.map((entry, index) => (
        <div key={index} className="d-flex align-items-center">
          <div style={{ width: 10, height: 10, backgroundColor: entry.color || entry.fill, marginRight: 6 }} />
          {entry.value || entry.name}
        </div>
      ))}
    </div>
  );

  return (
    <div className="admin-reports-container p-1 text-light overflow-hidden">
      <style>{`
        @media print { .no-print { display: none !important; } .bg-black { background: white !important; border: 1px solid #eee !important; color: black !important; } }
        .pulse-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 8px; text-align: center; }
        .chart-container { background: #000; border: 1px solid #333; border-radius: 8px; padding: 10px; margin-bottom: 15px; }
        .font-11 { font-size: 11px; }
        .lgreen { color: #52e88a !important; }
        @media (max-width: 576px) {
          .pulse-card { padding: 4px; }
        }
      `}</style>

      {/* EXPIRATION WINDOW DATE FILTER CONTROLS ONLY */}
      <div className="chart-container mb-3 p-2 p-sm-3 no-print">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
          <span className="small text-muted font-11 text-uppercase fw-bold d-flex align-items-center gap-1">
            <FaFilter className="text-info" /> REPORT SCOPE TOGGLES:
          </span>
          <div className="d-flex align-items-center gap-1">
            <Badge bg="info" className="text-dark fw-bold px-2 py-1" style={{ fontSize: '10px' }}>
              Showing {filteredProfiles.length} of {baseProfiles.length}
            </Badge>
            {selectedExpWindow !== 'all' && (
              <Button size="sm" variant="outline-warning" className="py-0 px-2 font-11 d-flex align-items-center gap-1" onClick={resetFilters}>
                <FaRedo size={9} /> Reset Filter
              </Button>
            )}
          </div>
        </div>

        {/* EXPIRATION WINDOW TOGGLES */}
        <div className="d-flex flex-wrap gap-1">
          {[
            { id: 'all', label: 'All Dates' },
            { id: 'thisMonth', label: 'This Month' },
            { id: '30days', label: 'Next 30 Days' },
            { id: '60days', label: 'Next 60 Days' }
          ].map(win => (
            <Button
              key={win.id}
              size="sm"
              variant={selectedExpWindow === win.id ? 'warning' : 'outline-secondary'}
              className={`font-11 py-0 px-2 rounded-pill ${selectedExpWindow === win.id ? 'text-dark fw-bold shadow-sm' : 'text-muted'}`}
              style={{ fontSize: '10px' }}
              onClick={() => setSelectedExpWindow(win.id)}
            >
              <FaCalendarAlt size={9} className="me-1" /> {win.label}
            </Button>
          ))}
        </div>
      </div>

      {/* PULSE METRICS ROW */}
      <div className="row g-1 g-sm-2 mb-3 no-print">
        <div className="col-3">
          <div className="pulse-card border-info shadow-sm">
            <div className="text-info text-uppercase mb-1" style={{ fontSize: '0.5rem', fontWeight: 'bold' }}>Collection</div>
            <div className="h6 mb-0 text-white fw-bold">{reportData.collectionRate}%</div>
            <div className="progress mt-1" style={{ height: '3px', background: '#333' }}>
              <div className="progress-bar bg-info" style={{ width: `${reportData.collectionRate}%` }}></div>
            </div>
          </div>
        </div>
        <div className="col-3">
          <div className="pulse-card border-success shadow-sm">
            <div className="text-success text-uppercase mb-1" style={{ fontSize: '0.5rem', fontWeight: 'bold' }}>ARPU</div>
            <div className="h6 mb-0 text-white fw-bold">${reportData.arpu}</div>
            <div className="text-white-50 mt-1" style={{ fontSize: '0.45rem' }}>Avg Yield</div>
          </div>
        </div>
        <div className="col-3">
          <div className="pulse-card border-warning shadow-sm">
            <div className="text-warning text-uppercase mb-1" style={{ fontSize: '0.5rem', fontWeight: 'bold' }}>Churn</div>
            <div className="h6 mb-0 text-white fw-bold">{reportData.churnRate}%</div>
            <div className="text-white-50 mt-1" style={{ fontSize: '0.45rem' }}>Expired</div>
          </div>
        </div>
        <div className="col-3">
          <div className="pulse-card border-secondary shadow-sm">
            <div className="text-secondary text-uppercase mb-1" style={{ fontSize: '0.5rem', fontWeight: 'bold' }}>Leaders</div>
            <div className="h6 mb-0 text-white fw-bold">{reportData.leadershipCount} <small style={{ fontSize: '0.55rem' }}>/{filteredProfiles.length}</small></div>
            <div className="text-white-50 mt-1" style={{ fontSize: '0.45rem' }}>Admins+Board</div>
          </div>
        </div>
      </div>

      {/* HEALTH & ROLE PIE CHARTS */}
      <div className="row g-0">
        <div className="col-12">
          <h6 className="text-center text-secondary text-uppercase " style={{ fontSize: '0.65rem' }}>Health Breakdown</h6>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={reportData.statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={55} dataKey="value" stroke="#fff" strokeWidth={0.5}>
                {reportData.statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const data = payload[0];
                  const percent = filteredProfiles.length > 0 ? ((data.value / filteredProfiles.length) * 100).toFixed(1) : 0;
                  return (
                    <div style={{ backgroundColor: '#0d0d0df2', border: '1px solid #444', padding: '8px', borderRadius: '6px', fontSize: '10px' }}>
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <span style={{ width: '8px', height: '8px', backgroundColor: data.fill, borderRadius: '2px', display: 'inline-block' }} />
                        <strong className="text-white">{data.name} Status</strong>
                      </div>
                      <div className="text-info fw-bold">{data.value} Members ({percent}%)</div>
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ marginBottom: "-25px" }} verticalAlign="bottom" content={(props) => renderCustomLegend(props.payload, "justify-content-center", "ps-0")} />
            </PieChart>
          </ResponsiveContainer>
          <hr />

          <h6 className="text-center text-secondary text-uppercase" style={{ fontSize: '0.65rem' }}>Role Densities</h6>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={reportData.roleData} cx="50%" cy="50%" innerRadius={40} outerRadius={55} dataKey="value" stroke="#fff" strokeWidth={0.5}>
                {reportData.roleData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const data = payload[0];
                  return (
                    <div style={{ backgroundColor: '#0d0d0df2', border: '1px solid #444', padding: '8px', borderRadius: '6px', fontSize: '10px' }}>
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <span style={{ width: '8px', height: '8px', backgroundColor: data.fill, borderRadius: '2px', display: 'inline-block' }} />
                        <strong className="text-white">{data.name} Role</strong>
                      </div>
                      <div className="text-info fw-bold">{data.value} Assigned Profiles</div>
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ marginBottom: "-25px" }} verticalAlign="bottom" content={(props) => renderCustomLegend(props.payload, "justify-content-center", "ps-0")} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <hr />

      {/* NEAR EXPIRATION RADAR CARD */}
      {reportData.expiringSoonList.length > 0 && (
        <div className="chart-container border-warning border-opacity-50">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
            <h6 className="text-warning text-uppercase m-0 d-flex align-items-center gap-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
              <FaExclamationTriangle /> Renewal Alert (60 Days)
            </h6>
            <Badge bg="warning" text="dark" className="fw-bold">{reportData.expiringSoonList.length} Members</Badge>
          </div>
          <div className="d-flex flex-wrap gap-1 custom-scrollbar" style={{ maxHeight: '100px', overflowY: 'auto' }}>
            {reportData.expiringSoonList.map((user, idx) => (
              <Badge key={idx} bg="dark" className="border border-secondary text-light fw-normal p-2 d-flex align-items-center gap-2" style={{ fontSize: '10px' }}>
                <span>{user.fullName}</span>
                <span className="text-warning fw-bold">({user.daysToExp}d left)</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* BUBBLE CHART */}
      <div className="chart-container mt-3">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Tier Analysis (Value & Density)</h6>
        <ResponsiveContainer width="100%" height={250}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis type="number" dataKey="revenue" name="Revenue" unit="$" stroke="#7a7a7a" fontSize={9} />
            <YAxis type="number" dataKey="members" name="Members" stroke="#787878" fontSize={9} />
            <ZAxis type="number" dataKey="z" range={[50, 400]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const p = payload[0].payload;
                return (
                  <div style={{ backgroundColor: '#0d0d0df2', border: '1px solid #444', padding: '10px', borderRadius: '6px', minWidth: '160px', fontSize: '10px' }}>
                    <div className="d-flex align-items-center gap-1 mb-1 pb-1 border-bottom border-secondary">
                      <span style={{ width: '8px', height: '8px', backgroundColor: p.fill, borderRadius: '2px', display: 'inline-block' }} />
                      <strong className="text-white">{p.name} Tier</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white-50">Members:</span>
                      <strong className="text-light">{p.members}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white-50">Worth:</span>
                      <strong className="text-success">${p.revenue.toLocaleString()}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white-50">Weight:</span>
                      <strong className="text-info">{p.weight}%</strong>
                    </div>
                    <div className="d-flex justify-content-between pt-1 border-top border-secondary">
                      <span className="text-white-50">Avg Yield:</span>
                      <strong className="text-warning">${p.avg}</strong>
                    </div>
                  </div>
                );
              }}
            />
            <Scatter name="Membership Levels" data={reportData.bubbleData}>
              {reportData.bubbleData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} stroke="#fff" strokeWidth={0.5} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        {renderCustomLegend(reportData.bubbleData, "justify-content-center", "ps-0")}
      </div>

      {/* ACTIVITY TREND */}
      <div className="chart-container">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Activity Trend (Volume vs. Value)</h6>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={reportData.monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis dataKey="fullLabel" stroke="#777" fontSize={8} angle={-45} textAnchor="end" interval={0} />
            <YAxis yAxisId="left" stroke="#777" fontSize={8} tickFormatter={(v) => `$${v}`} />
            <YAxis yAxisId="right" orientation="right" stroke="#3296aaa2" fontSize={8} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const rev = payload.find(p => p.dataKey === 'totalRevenue')?.value || 0;
                const count = payload.find(p => p.dataKey === 'memberCount')?.value || 0;
                const avgYield = count > 0 ? (rev / count).toFixed(2) : 0;
                return (
                  <div style={{ backgroundColor: '#0d0d0df2', border: '1px solid #444', padding: '10px', borderRadius: '6px', minWidth: '160px', fontSize: '10px' }}>
                    <div className="text-info fw-bold pb-1 mb-1 border-bottom border-secondary">{label} Projection</div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white-50">Revenue:</span>
                      <strong className="text-success">${rev.toLocaleString()}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white-50">Volume:</span>
                      <strong className="text-info">{count} Expirations</strong>
                    </div>
                    <div className="d-flex justify-content-between pt-1 border-top border-secondary">
                      <span className="text-white-50">Avg Yield:</span>
                      <strong className="text-warning">${avgYield}/member</strong>
                    </div>
                  </div>
                );
              }}
            />
            <Area yAxisId="left" type="monotone" dataKey="totalRevenue" fill="rgba(181, 77, 122, 0.1)" stroke="#b54d7ac9" strokeWidth={2} />
            <Line yAxisId="right" type="step" dataKey="memberCount" stroke="#3296aaa2" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* STACKED REVENUE */}
      <div className="chart-container">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem' }}>12-Month Revenue Outlook</h6>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={reportData.monthlyData} margin={{ top: 20, right: 10, left: -25, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis dataKey="fullLabel" stroke="#777" fontSize={8} angle={-45} textAnchor="end" interval={0} />
            <YAxis stroke="#777" fontSize={8} tickFormatter={(v) => `$${v}`} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.08)' }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;

                const monthTotal = payload[0]?.payload?.totalRevenue || 0;
                const totalMembers = payload[0]?.payload?.memberCount || 0;

                const activeTiers = payload
                  .filter(p => p.value > 0)
                  .sort((a, b) => b.value - a.value);

                return (
                  <div style={{
                    backgroundColor: '#0d0d0df2',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    padding: '10px',
                    minWidth: '180px',
                    fontSize: '10px'
                  }}>
                    <div className="d-flex justify-content-between align-items-center pb-1 mb-1 border-bottom border-secondary">
                      <strong className="text-info fw-bold">{label} Outlook</strong>
                      <span className="badge bg-info text-dark">${monthTotal.toLocaleString()}</span>
                    </div>

                    <div className="text-white-50 mb-1 d-flex justify-content-between">
                      <span>Renewals:</span>
                      <span className="text-light fw-bold">{totalMembers} Members</span>
                    </div>

                    <div className="d-flex flex-column gap-1">
                      {activeTiers.length > 0 ? (
                        activeTiers.map((entry, idx) => (
                          <div key={idx} className="d-flex align-items-center justify-content-between py-1 border-bottom border-secondary border-opacity-10">
                            <div className="d-flex align-items-center gap-1 text-truncate" style={{ maxWidth: '120px' }}>
                              <span style={{ width: '8px', height: '8px', backgroundColor: entry.color, borderRadius: '2px', display: 'inline-block' }} />
                              <span className="text-light text-truncate" style={{ fontSize: '10px' }}>{entry.name}</span>
                            </div>
                            <strong className="text-success ms-2">
                              ${entry.value.toLocaleString()}
                            </strong>
                          </div>
                        ))
                      ) : (
                        <div className="text-muted italic text-center py-1">No renewals scheduled</div>
                      )}
                    </div>
                  </div>
                );
              }}
            />
            <Legend verticalAlign="top" content={(props) => renderCustomLegend(props.payload, "justify-content-start", "ps-4")} />
            {reportData.membershipTypes.map((type, index) => (
              <Bar key={type} dataKey={type} stackId="a" fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={0.3}>
                {index === reportData.membershipTypes.length - 1 && <LabelList dataKey="totalRevenue" position="top" formatter={(v) => v > 0 ? `$${v}` : ''} style={{ fill: '#ccc', fontSize: '9px', fontWeight: 'bold' }} />}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div className="text-center text-white-50 mt-1" style={{ fontSize: '0.55rem', fontStyle: 'italic' }}>(Excludes flagged accounts and incidents)</div>
      </div>

      <hr className="border-secondary opacity-25 white" />

      {/* EXECUTIVE FINANCIAL MATRIX TABLE */}
      <div className="chart-container p-2 p-sm-3">
        <h6 className="text-white text-uppercase mb-2 d-flex align-items-center gap-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
          <FaDollarSign className="text-success" /> Tier Revenue & Density Matrix
        </h6>
        <div className="table-responsive">
          <Table size="sm" variant="dark" hover className="m-0 align-middle" style={{ fontSize: '10px' }}>
            <thead>
              <tr className="border-bottom border-secondary text-muted text-uppercase">
                <th>Tier Level</th>
                <th className="text-center">Count</th>
                <th className="text-center">Rate</th>
                <th className="text-end">Worth</th>
                <th className="text-end">Weight</th>
              </tr>
            </thead>
            <tbody>
              {[...reportData.bubbleData].sort((a, b) => b.revenue - a.revenue).map((tier, idx) => (
                <tr key={idx} className="border-bottom border-secondary border-opacity-10">
                  <td className="fw-bold text-info text-truncate" style={{ maxWidth: '90px' }}>
                    <span style={{ width: '6px', height: '6px', backgroundColor: tier.fill, display: 'inline-block', borderRadius: '50%', marginRight: '4px' }} />
                    {tier.name}
                  </td>
                  <td className="text-center text-light">{tier.members}</td>
                  <td className="text-center text-muted">${tier.cost}</td>
                  <td className="text-end fw-bold text-success">${tier.revenue.toLocaleString()}</td>
                  <td className="text-end text-white-50">{tier.weight}%</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* INTERACTIVE RETENTION SCENARIO SIMULATOR */}
      <div className="chart-container p-2 p-sm-3 border-info border-opacity-25">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
          <h6 className="text-info text-uppercase m-0 d-flex align-items-center gap-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
            <FaSlidersH /> Retention Forecast Simulator
          </h6>
          <Badge bg={retentionSimulation >= 0 ? "success" : "danger"} className="fw-bold font-11">
            {retentionSimulation >= 0 ? `+${retentionSimulation}% Growth` : `${retentionSimulation}% Retention`}
          </Badge>
        </div>
        <p className="text-white-50 mb-2" style={{ fontSize: '9px' }}>
          Simulate revenue impact based on retention marketing or rate adjustments:
        </p>
        <Form.Range
          min={-20}
          max={30}
          step={5}
          value={retentionSimulation}
          onChange={(e) => setRetentionSimulation(Number(e.target.value))}
          className="mb-2"
        />
        <div className="d-flex justify-content-between align-items-center bg-dark p-2 rounded border border-secondary" style={{ fontSize: '10px' }}>
          <span className="text-muted">Simulated Revenue:</span>
          <strong className="text-success font-11">${reportData.simulatedRevenue.toLocaleString()}</strong>
        </div>
      </div>

      {/* POTENTIAL REVENUE BREAKDOWN */}
      <div className="mt-3 p-3 chart-container" style={{ fontSize: '0.75rem' }}>
        <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.7rem', letterSpacing: '1px' }} title="Base Cost + Late Fee for Expired Members | Base Cost only for New Users">Potential Revenue Breakdown ⓘ</h6>
        {Object.entries(reportData.financialBreakdown).sort().map(([t, d]) => (
          <div key={t} className="col-12 d-flex justify-content-between border-bottom border-secondary pb-1 mb-1">
            <span className="text-white-50">{d.count} {t}</span>
            <span className="text-success fw-bold">${d.revenue.toLocaleString()}</span>
          </div>
        ))}
        <div className="col-12 d-flex justify-content-between pt-2 mt-1 rounded bg-white bg-opacity-10 p-2 text-white fw-bold">
          <span>Total Potential</span>
          <span className="text-success fw-bold">${reportData.totalRevenue.toLocaleString()}</span>
        </div>
      </div>

      {/* LOSSES/NEW */}
      <div className="mt-3 p-3 chart-container" style={{ fontSize: '0.75rem' }}>
        <h6 className="text-danger text-uppercase mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold', letterSpacing: '1px' }} title="Reflects the exact potential value lost (includes late fees for existing members only)">Expired Losses or New Users that have not paid yet ⓘ</h6>
        {Object.entries(reportData.lostRevenueBreakdown).sort().map(([t, d]) => (
          <div key={t} className="col-12 d-flex justify-content-between border-bottom border-secondary pb-1 mb-1 text-white">
            <span>{d.count} {t} ({d.isNew ? 'New' : 'Expired'})</span>
            <span className="text-danger-subtle fw-bold">-${d.loss.toLocaleString()}</span>
          </div>
        ))}
        <div className="col-12 d-flex justify-content-between pt-2 mt-1 rounded bg-danger bg-opacity-10 p-2 text-white fw-bold shadow-sm">
          <span>Net Loss</span>
          <span className="text-danger fw-bold">-${reportData.totalLoss.toLocaleString()}</span>
        </div>
      </div>

      {/* NET EXPECTED */}
      <div className="mt-3 p-3 rounded bg-info bg-opacity-10 border border-info mb-4 shadow-sm" style={{ fontSize: '0.8rem' }}>
        <div className="d-flex justify-content-between align-items-center text-info text-uppercase fw-bold">
          <span style={{ letterSpacing: '0.5px' }}>Net Expected Revenue</span>
          <span style={{ fontSize: '1.1rem' }} className="text-info fw-bold">${reportData.netPotential.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminCheckReportz;